import type { PromptType } from '../types/game';
import type { TranslationKey } from '../i18n/keys';
import type { PlayerGender } from './coupleSetup';
import { readCoupleSetupFromSession } from './coupleSetup';
import type { GameSession } from '../store/slices/gameSlice';

export interface PlayerGenders {
  player1?: PlayerGender | null;
  player2?: PlayerGender | null;
}

function genderForPlayer(
  player: 'player1' | 'player2',
  genders: PlayerGenders
): PlayerGender | null {
  return player === 'player1'
    ? (genders.player1 ?? null)
    : (genders.player2 ?? null);
}

/**
 * Maps the active player's gender to prompt direction.
 * Same-gender couples use `both` until dedicated decks exist.
 */
export function resolvePromptDirectionType(
  gameMode: 'solo' | 'couple' | null | undefined,
  currentPlayer: 'player1' | 'player2',
  genders: PlayerGenders = {}
): PromptType | undefined {
  if (gameMode !== 'couple') {
    return undefined;
  }

  const g1 = genders.player1 ?? 'male';
  const g2 = genders.player2 ?? 'female';

  if (g1 === g2) {
    return 'both';
  }

  const activeGender = genderForPlayer(currentPlayer, {
    player1: g1,
    player2: g2,
  });

  if (activeGender === 'male') {
    return 'male_to_female';
  }
  if (activeGender === 'female') {
    return 'female_to_male';
  }

  return currentPlayer === 'player1' ? 'male_to_female' : 'female_to_male';
}

export function resolveGendersFromGameState(game: {
  playerGenders?: PlayerGenders | null;
  currentSession?: GameSession | null;
}): PlayerGenders {
  const fromSession = readCoupleSetupFromSession(
    game.currentSession?.flow_state as Record<string, unknown> | undefined
  );

  return {
    player1: fromSession?.player1_gender ?? game.playerGenders?.player1 ?? null,
    player2: fromSession?.player2_gender ?? game.playerGenders?.player2 ?? null,
  };
}

/** Translation key for the direction badge, or undefined for unknown types. */
export function promptDirectionLabelKey(
  type: string | undefined
): TranslationKey | undefined {
  switch (type) {
    case 'male_to_female':
      return 'scene.generic.direction.maleToFemale';
    case 'female_to_male':
      return 'scene.generic.direction.femaleToMale';
    case 'both':
      return 'scene.generic.direction.both';
    case 'general':
      return 'scene.generic.direction.general';
    default:
      return undefined;
  }
}
